import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { subjectsAPI } from '../services/api';
import { PencilSquare, Trash, Plus, ChevronDown, ChevronRight, Search, ArrowUp, ArrowDown, InfoCircleFill } from 'react-bootstrap-icons';
import CoursesIcon from '../img/course.gif';
import SubjectsGuideModal from '../Components/SubjectsGuideModal';
import '../CSS/Subjects.css';

function Courses() {
  const { user } = useSelector(state => state.auth);
  const userRole = user?.role || '';
  const [data, setData] = useState({ classes: [], naStreams: [] });
  const [expanded, setExpanded] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [parentId, setParentId] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    paces: '',
    paceNumbers: '',
    credits: '',
    class_id: null
  });
  const [validationMessage, setValidationMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'class',
    direction: 'asc'
  });
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (successMessage) setSuccessMessage('');
      if (validationMessage) setValidationMessage('');
    }, 3000);
    return () => clearTimeout(timer);
  }, [successMessage, validationMessage]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await subjectsAPI.getSubjects();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setValidationMessage('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const calculateTotals = (subjects) => {
    return subjects.reduce((totals, subject) => {
      return {
        totalPaces: totals.totalPaces + (parseInt(subject.paces) || 0),
        totalCredits: totals.totalCredits + (parseInt(subject.credits) || 0)
      };
    }, { totalPaces: 0, totalCredits: 0 });
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setSortDropdownOpen(false);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ArrowUp /> : <ArrowDown />;
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;

    const dataCopy = {
      classes: [...data.classes],
      naStreams: [...data.naStreams]
    };

    if (sortConfig.key === 'class') {
      dataCopy.classes.sort((a, b) => {
        const numA = parseInt(a.name);
        const numB = parseInt(b.name);
        return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
      });
    }

    dataCopy.classes = dataCopy.classes.map(cls => ({
      ...cls,
      streams: [...cls.streams].sort((a, b) => {
        if (sortConfig.key === 'stream') {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          return sortConfig.direction === 'asc'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        }
        return 0;
      }).map(stream => ({
        ...stream,
        subjects: [...stream.subjects].sort((a, b) => {
          switch (sortConfig.key) {
            case 'subject': {
              const nameA = a.name.toLowerCase();
              const nameB = b.name.toLowerCase();
              return sortConfig.direction === 'asc'
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
            }
            case 'paces':
              return sortConfig.direction === 'asc' ? a.paces - b.paces : b.paces - a.paces;
            case 'credits':
              return sortConfig.direction === 'asc' ? a.credits - b.credits : b.credits - a.credits;
            default:
              return 0;
          }
        })
      }))
    }));

    dataCopy.naStreams = dataCopy.naStreams.sort((a, b) => {
      if (sortConfig.key === 'stream') {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return sortConfig.direction === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
      return 0;
    }).map(stream => ({
      ...stream,
      subjects: [...stream.subjects].sort((a, b) => {
        switch (sortConfig.key) {
          case 'subject': {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            return sortConfig.direction === 'asc'
              ? nameA.localeCompare(nameB)
              : nameB.localeCompare(nameA);
          }
          case 'paces':
            return sortConfig.direction === 'asc' ? a.paces - b.paces : b.paces - a.paces;
          case 'credits':
            return sortConfig.direction === 'asc' ? a.credits - b.credits : b.credits - a.credits;
          default:
            return 0;
        }
      })
    }));

    return dataCopy;
  };

  const filterData = (data, query) => {
    if (!query) return data;

    const filteredClasses = data.classes.filter(cls =>
      cls.name.toLowerCase().includes(query) ||
      cls.streams.some(stream =>
        stream.name.toLowerCase().includes(query) ||
        stream.subjects.some(subject =>
          subject.name.toLowerCase().includes(query) ||
          subject.paces.toString().includes(query) ||
          subject.paceNumbers?.toString().includes(query) ||
          subject.credits.toString().includes(query)
        ))
    ).map(cls => ({
      ...cls,
      streams: cls.streams.filter(stream =>
        stream.name.toLowerCase().includes(query) ||
        stream.subjects.some(subject =>
          subject.name.toLowerCase().includes(query) ||
          subject.paces.toString().includes(query) ||
          subject.paceNumbers?.toString().includes(query) ||
          subject.credits.toString().includes(query))
      ).map(stream => ({
        ...stream,
        subjects: stream.subjects.filter(subject =>
          subject.name.toLowerCase().includes(query) ||
          subject.paces.toString().includes(query) ||
          subject.paceNumbers?.toString().includes(query) ||
          subject.credits.toString().includes(query)
        )
      }))
    }));

    const filteredNaStreams = data.naStreams.filter(stream =>
      stream.name.toLowerCase().includes(query) ||
      stream.subjects.some(subject =>
        subject.name.toLowerCase().includes(query) ||
        subject.paces.toString().includes(query) ||
        subject.paceNumbers?.toString().includes(query) ||
        subject.credits.toString().includes(query)
      )
    ).map(stream => ({
      ...stream,
      subjects: stream.subjects.filter(subject =>
        subject.name.toLowerCase().includes(query) ||
        subject.paces.toString().includes(query) ||
        subject.paceNumbers?.toString().includes(query) ||
        subject.credits.toString().includes(query)
      )
    }));

    return {
      classes: filteredClasses,
      naStreams: filteredNaStreams
    };
  };

  const processedData = sortData(filterData(data, searchQuery.toLowerCase()));
  const hasResults = processedData.classes.length > 0 || processedData.naStreams.length > 0;

  const validateClass = (value) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) return "Class must be a number";
    if (numValue < 6 || numValue > 12) return "Class must be between 6 and 12";
    return "";
  };

  const validateStream = (value) => {
    if (!/^[a-zA-Z\s]+$/.test(value)) return "Stream must contain only letters and spaces";
    if (value.trim().length === 0) return "Stream name cannot be empty";
    return "";
  };

  const validateSubject = (value) => {
    if (!/^[a-zA-Z\s]+$/.test(value)) return "Subject must contain only letters and spaces";
    if (value.trim().length === 0) return "Subject name cannot be empty";
    return "";
  };

  const validatePaces = (value) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) return "Paces must be a number";
    if (numValue <= 0) return "Paces must be greater than 0";
    if (numValue > 50) return "Paces cannot exceed 50";
    return "";
  };

  const validatePaceNumbers = (value) => {
    if (!value || value.trim() === '') return "";
    if (/^\d+$/.test(value)) return "";
    if (/^\d+\s*-\s*\d+$/.test(value)) {
      const [start, end] = value.split('-').map(num => parseInt(num.trim()));
      if (start >= end) return "Range start must be less than end";
      return "";
    }
    return "Pace numbers must be a single number (e.g., 1097) or range (e.g., 1097-1108)";
  };

  const validateCredits = (value) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) return "Credits must be a number";
    if (numValue <= 0) return "Credits must be greater than 0";
    if (numValue > 50) return "Credits cannot exceed 50";
    return "";
  };

  const handleAddClick = (type, parentId = null) => {
    setModalType(type);
    setParentId(parentId);
    setFormData({ name: '', paces: '', paceNumbers: '', credits: '', class_id: parentId || null });
    setShowAddModal(true);
    setValidationMessage('');
  };

  const handleEditClick = (type, item, parentId = null) => {
    setModalType(type);
    setParentId(parentId);
    setCurrentItem(item);
    setFormData({
      name: item.name,
      paces: item.paces || '',
      paceNumbers: item.paceNumbers || '',
      credits: item.credits || '',
      class_id: parentId || null
    });
    setShowEditModal(true);
    setValidationMessage('');
  };

  const handleDeleteClick = (type, item) => {
    setModalType(type);
    setCurrentItem(item);
    setShowDeleteModal(true);
  };

  const validateForm = () => {
    let error = '';
    if (modalType === 'class') {
      error = validateClass(formData.name);
    } else if (modalType === 'stream') {
      error = validateStream(formData.name);
    } else if (modalType === 'subject') {
      error = validateSubject(formData.name);
      if (!error) error = validatePaces(formData.paces);
      if (!error) error = validatePaceNumbers(formData.paceNumbers);
      if (!error) error = validateCredits(formData.credits);
    }
    if (error) {
      setValidationMessage(error);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      if (modalType === 'class') {
        await subjectsAPI.createClass({ name: formData.name });
      } else if (modalType === 'stream') {
        await subjectsAPI.createStream({ name: formData.name, class_id: formData.class_id });
      } else if (modalType === 'subject') {
        await subjectsAPI.createSubject({
          stream_id: parentId,
          name: formData.name,
          paces: parseInt(formData.paces),
          pace_numbers: formData.paceNumbers || null,
          credits: parseInt(formData.credits)
        });
      }
      setSuccessMessage(`${modalType.charAt(0).toUpperCase() + modalType.slice(1)} added successfully`);
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      console.error('Error adding data:', error);
      setValidationMessage(error.response?.data?.error || 'Failed to add');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    try {
      if (modalType === 'class') {
        await subjectsAPI.updateClass(currentItem.id, { name: formData.name });
      } else if (modalType === 'stream') {
        await subjectsAPI.updateStream(currentItem.id, { name: formData.name, class_id: formData.class_id });
      } else if (modalType === 'subject') {
        await subjectsAPI.updateSubject(currentItem.id, {
          name: formData.name,
          paces: parseInt(formData.paces),
          pace_numbers: formData.paceNumbers || null,
          credits: parseInt(formData.credits),
          stream_id: parentId
        });
      }
      setSuccessMessage(`${modalType.charAt(0).toUpperCase() + modalType.slice(1)} updated successfully`);
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      console.error('Error updating data:', error);
      setValidationMessage(error.response?.data?.error || 'Failed to update');
    }
  };

  const handleDelete = async () => {
    try {
      if (modalType === 'class') {
        await subjectsAPI.deleteClass(currentItem.id);
      } else if (modalType === 'stream') {
        await subjectsAPI.deleteStream(currentItem.id);
      } else if (modalType === 'subject') {
        await subjectsAPI.deleteSubject(currentItem.id);
      }
      setSuccessMessage(`${modalType.charAt(0).toUpperCase() + modalType.slice(1)} deleted successfully`);
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting data:', error);
      setValidationMessage(error.response?.data?.error || 'Failed to delete');
    }
  };

  return (
    <div className="sbjt-page">

      {/* ── Toast Notifications ── */}
      <div className="sbjt-toast-region" aria-live="polite" aria-atomic="true">
        <div className={`sbjt-toast sbjt-toast-success ${successMessage ? 'sbjt-toast-show' : ''}`} role="alert">
          <div className="sbjt-toast-header">
            <span className="sbjt-toast-title">Success</span>
            <button className="sbjt-toast-close" onClick={() => setSuccessMessage('')} aria-label="Close">×</button>
          </div>
          <div className="sbjt-toast-body">{successMessage}</div>
        </div>

        <div className={`sbjt-toast sbjt-toast-danger ${validationMessage ? 'sbjt-toast-show' : ''}`} role="alert">
          <div className="sbjt-toast-header">
            <span className="sbjt-toast-title">Error</span>
            <button className="sbjt-toast-close" onClick={() => setValidationMessage('')} aria-label="Close">×</button>
          </div>
          <div className="sbjt-toast-body">{validationMessage}</div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="sbjt-inner-container">

        {/* Staff alert */}
        {userRole === 'staff' && (
          <div className="sbjt-staff-alert">
            <InfoCircleFill />
            You have view-only access as a staff member.
          </div>
        )}

        {/* Page heading */}
        <div className="row my-4">
          <div className="col-12">
            <h3 className="sbjt-heading-title">
              Subjects &amp; Streams
              <img className="sbjt-animated-icon" src={CoursesIcon} alt="Course icon" />
            </h3>
          </div>
      
         
        </div>

        {/* Search */}
        <div className="sbjt-search-wrapper">
          <Search className="sbjt-search-icon" />
          <input
            type="text"
            className="sbjt-search-input"
            placeholder="Search..."
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <h4 className="sbjt-section-label">Subjects &amp; Streams Structure</h4>

        {/* Toolbar */}
        <div className="sbjt-toolbar">
          {/* Sort dropdown */}
          <div className="sbjt-dropdown">
            <button
              className="sbjt-dropdown-toggle sbjt-dropdown-toggle--warning"
              onClick={() => setSortDropdownOpen(prev => !prev)}
            >
              Sort {getSortIcon(sortConfig.key)}
              <span className="sbjt-dropdown-arrow">▾</span>
            </button>
            {sortDropdownOpen && (
              <div className="sbjt-dropdown-menu">
                <button className="sbjt-dropdown-item" onClick={() => requestSort('class')}>
                  Class Name {sortConfig.key === 'class' && getSortIcon('class')}
                </button>
                <button className="sbjt-dropdown-item" onClick={() => requestSort('stream')}>
                  Stream Name {sortConfig.key === 'stream' && getSortIcon('stream')}
                </button>
                <button className="sbjt-dropdown-item" onClick={() => requestSort('subject')}>
                  Subject Name {sortConfig.key === 'subject' && getSortIcon('subject')}
                </button>
                <button className="sbjt-dropdown-item" onClick={() => requestSort('paces')}>
                  Paces {sortConfig.key === 'paces' && getSortIcon('paces')}
                </button>
                <button className="sbjt-dropdown-item" onClick={() => requestSort('credits')}>
                  Credits {sortConfig.key === 'credits' && getSortIcon('credits')}
                </button>
              </div>
            )}
          </div>

          {userRole === 'admin' && (
            <button className="sbjt-btn sbjt-btn-success" onClick={() => handleAddClick('class')}>
              <Plus /> Add Class
            </button>
          )}
        </div>

        {/* Table section */}
        <section className="sbjt-table-section">
          <div className="sbjt-table-container">
            <div className="sbjt-table-wrapper">
              <table className="sbjt-table">
                <thead>
                  <tr className="sbjt-table-header">
                    <th>
                      <button className="sbjt-sort-btn" onClick={() => requestSort('class')}>
                        Class {getSortIcon('class')}
                      </button>
                    </th>
                    <th>
                      <button className="sbjt-sort-btn" onClick={() => requestSort('stream')}>
                        Stream {getSortIcon('stream')}
                      </button>
                    </th>
                    <th>
                      <button className="sbjt-sort-btn" onClick={() => requestSort('subject')}>
                        Subject {getSortIcon('subject')}
                      </button>
                    </th>
                    <th>
                      <button className="sbjt-sort-btn" onClick={() => requestSort('paces')}>
                        Paces {getSortIcon('paces')}
                      </button>
                    </th>
                    <th>Pace Numbers</th>
                    <th>
                      <button className="sbjt-sort-btn" onClick={() => requestSort('credits')}>
                        Credits {getSortIcon('credits')}
                      </button>
                    </th>
                    {userRole === 'admin' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody className="sbjt-table-body">
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="sbjt-loading-state">
                        <div className="sbjt-loader-wrap">
                          <div className="sbjt-loader-ring" />
                          <span className="sbjt-loader-text">Loading Subjects &amp; Streams</span>
                        </div>
                      </td>
                    </tr>
                  ) : !hasResults ? (
                    <tr>
                      <td colSpan="7" className="sbjt-empty-state">
                        <InfoCircleFill className="sbjt-empty-icon" />
                        No results found for &ldquo;{searchQuery}&rdquo;
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* N/A streams (no class) */}
                      {processedData.naStreams?.map(stream => (
                        <React.Fragment key={`na-${stream.id}`}>
                          <tr className="sbjt-row-stream">
                            <td className="sbjt-cell">N/A</td>
                            <td className="sbjt-cell">
                              <button
                                className="sbjt-expand-btn"
                                onClick={() => toggleExpand(`na-${stream.id}`)}
                              >
                                {expanded[`na-${stream.id}`] ? <ChevronDown /> : <ChevronRight />}
                                {stream.name}
                              </button>
                              {userRole === 'admin' && (
                                <span className="sbjt-cell-actions">
                                  <button
                                    className="sbjt-btn sbjt-btn-sm sbjt-btn-outline-success"
                                    onClick={() => handleAddClick('subject', stream.id)}
                                  >
                                    <Plus size={12} /> Subject
                                  </button>
                                  <button
                                    className="sbjt-action-btn sbjt-action-edit"
                                    onClick={() => handleEditClick('stream', stream)}
                                    title="Edit stream"
                                  >
                                    <PencilSquare size={12} />
                                  </button>
                                  <button
                                    className="sbjt-action-btn sbjt-action-delete"
                                    onClick={() => handleDeleteClick('stream', stream)}
                                    title="Delete stream"
                                  >
                                    <Trash size={12} />
                                  </button>
                                </span>
                              )}
                            </td>
                            <td colSpan="5"></td>
                          </tr>

                          {expanded[`na-${stream.id}`] && stream.subjects?.map(subject => (
                            <tr key={`na-subject-${subject.id}`} className="sbjt-row-subject">
                              <td className="sbjt-cell"></td>
                              <td className="sbjt-cell"></td>
                              <td className="sbjt-cell">{subject.name}</td>
                              <td className="sbjt-cell">{subject.paces}</td>
                              <td className="sbjt-cell">{subject.paceNumbers || '-'}</td>
                              <td className="sbjt-cell">{subject.credits}</td>
                              {userRole === 'admin' && (
                                <td className="sbjt-cell">
                                  <span className="sbjt-cell-actions">
                                    <button
                                      className="sbjt-action-btn sbjt-action-edit"
                                      onClick={() => handleEditClick('subject', subject, stream.id)}
                                      title="Edit subject"
                                    >
                                      <PencilSquare size={12} />
                                    </button>
                                    <button
                                      className="sbjt-action-btn sbjt-action-delete"
                                      onClick={() => handleDeleteClick('subject', subject)}
                                      title="Delete subject"
                                    >
                                      <Trash size={12} />
                                    </button>
                                  </span>
                                </td>
                              )}
                            </tr>
                          ))}

                          {/* Totals row for N/A stream */}
                          <tr className="sbjt-row-totals">
                            <td className="sbjt-cell"></td>
                            <td className="sbjt-cell"></td>
                            <td className="sbjt-cell sbjt-totals-label">Total</td>
                            <td className="sbjt-cell">{calculateTotals(stream.subjects).totalPaces}</td>
                            <td className="sbjt-cell"></td>
                            <td className="sbjt-cell">{calculateTotals(stream.subjects).totalCredits}</td>
                            {userRole === 'admin' && <td className="sbjt-cell"></td>}
                          </tr>
                        </React.Fragment>
                      ))}

                      {/* Classes with streams and subjects */}
                      {processedData.classes?.map(cls => (
                        <React.Fragment key={`class-${cls.id}`}>
                          <tr className="sbjt-row-class">
                            <td className="sbjt-cell">
                              <button
                                className="sbjt-expand-btn"
                                onClick={() => toggleExpand(`class-${cls.id}`)}
                              >
                                {expanded[`class-${cls.id}`] ? <ChevronDown /> : <ChevronRight />}
                                {cls.name}
                              </button>
                              {userRole === 'admin' && (
                                <span className="sbjt-cell-actions">
                                  <button
                                    className="sbjt-btn sbjt-btn-sm sbjt-btn-outline-success"
                                    onClick={() => handleAddClick('stream', cls.id)}
                                  >
                                    <Plus size={12} /> Stream
                                  </button>
                                  <button
                                    className="sbjt-action-btn sbjt-action-edit"
                                    onClick={() => handleEditClick('class', cls)}
                                    title="Edit class"
                                  >
                                    <PencilSquare size={12} />
                                  </button>
                                  <button
                                    className="sbjt-action-btn sbjt-action-delete"
                                    onClick={() => handleDeleteClick('class', cls)}
                                    title="Delete class"
                                  >
                                    <Trash size={12} />
                                  </button>
                                </span>
                              )}
                            </td>
                            <td colSpan="6"></td>
                          </tr>

                          {expanded[`class-${cls.id}`] && cls.streams?.map(stream => (
                            <React.Fragment key={`stream-${stream.id}`}>
                              <tr className="sbjt-row-stream">
                                <td className="sbjt-cell"></td>
                                <td className="sbjt-cell">
                                  <button
                                    className="sbjt-expand-btn"
                                    onClick={() => toggleExpand(`stream-${stream.id}`)}
                                  >
                                    {expanded[`stream-${stream.id}`] ? <ChevronDown /> : <ChevronRight />}
                                    {stream.name}
                                  </button>
                                  {userRole === 'admin' && (
                                    <span className="sbjt-cell-actions">
                                      <button
                                        className="sbjt-btn sbjt-btn-sm sbjt-btn-outline-success"
                                        onClick={() => handleAddClick('subject', stream.id)}
                                      >
                                        <Plus size={12} /> Subject
                                      </button>
                                      <button
                                        className="sbjt-action-btn sbjt-action-edit"
                                        onClick={() => handleEditClick('stream', stream, cls.id)}
                                        title="Edit stream"
                                      >
                                        <PencilSquare size={12} />
                                      </button>
                                      <button
                                        className="sbjt-action-btn sbjt-action-delete"
                                        onClick={() => handleDeleteClick('stream', stream)}
                                        title="Delete stream"
                                      >
                                        <Trash size={12} />
                                      </button>
                                    </span>
                                  )}
                                </td>
                                <td colSpan="5"></td>
                              </tr>

                              {expanded[`stream-${stream.id}`] && stream.subjects?.map(subject => (
                                <tr key={`subject-${subject.id}`} className="sbjt-row-subject">
                                  <td className="sbjt-cell"></td>
                                  <td className="sbjt-cell"></td>
                                  <td className="sbjt-cell">{subject.name}</td>
                                  <td className="sbjt-cell">{subject.paces}</td>
                                  <td className="sbjt-cell">{subject.pace_numbers || '-'}</td>
                                  <td className="sbjt-cell">{subject.credits}</td>
                                  {userRole === 'admin' && (
                                    <td className="sbjt-cell">
                                      <span className="sbjt-cell-actions">
                                        <button
                                          className="sbjt-action-btn sbjt-action-edit"
                                          onClick={() => handleEditClick('subject', subject, stream.id)}
                                          title="Edit subject"
                                        >
                                          <PencilSquare size={12} />
                                        </button>
                                        <button
                                          className="sbjt-action-btn sbjt-action-delete"
                                          onClick={() => handleDeleteClick('subject', subject)}
                                          title="Delete subject"
                                        >
                                          <Trash size={12} />
                                        </button>
                                      </span>
                                    </td>
                                  )}
                                </tr>
                              ))}

                              {/* Totals row for stream */}
                              <tr className="sbjt-row-totals">
                                <td className="sbjt-cell" colSpan="2"></td>
                                <td className="sbjt-cell sbjt-totals-label">Total</td>
                                <td className="sbjt-cell">{calculateTotals(stream.subjects).totalPaces}</td>
                                <td className="sbjt-cell"></td>
                                <td className="sbjt-cell">{calculateTotals(stream.subjects).totalCredits}</td>
                                {userRole === 'admin' && <td className="sbjt-cell"></td>}
                              </tr>
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── Add Modal ── */}
        {showAddModal && (
          <div className="sbjt-modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="sbjt-modal-content" onClick={e => e.stopPropagation()}>
              <div className="sbjt-modal-header sbjt-modal-header--info">
                <h5 className="sbjt-modal-title">
                  Add {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
                </h5>
                <button className="sbjt-modal-close" onClick={() => setShowAddModal(false)} aria-label="Close">×</button>
              </div>
              <div className="sbjt-modal-body">
                {validationMessage && (
                  <div className="sbjt-form-alert sbjt-form-alert--danger">{validationMessage}</div>
                )}
                <div className="sbjt-form-field">
                  <input
                    className="sbjt-form-input"
                    type={modalType === 'class' ? 'number' : 'text'}
                    placeholder=" "
                    value={formData.name}
                    min={modalType === 'class' ? 6 : undefined}
                    max={modalType === 'class' ? 12 : undefined}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onBlur={() => {
                      let error = '';
                      if (modalType === 'class') error = validateClass(formData.name);
                      else if (modalType === 'stream') error = validateStream(formData.name);
                      else if (modalType === 'subject') error = validateSubject(formData.name);
                      if (error) setValidationMessage(error);
                    }}
                  />
                  <label className="sbjt-form-label">
                    {modalType.charAt(0).toUpperCase() + modalType.slice(1)} Name
                  </label>
                </div>

                {modalType === 'subject' && (
                  <>
                    <div className="sbjt-form-field">
                      <input
                        className="sbjt-form-input"
                        type="number"
                        placeholder=" "
                        min="1"
                        max="50"
                        value={formData.paces}
                        onChange={(e) => setFormData({ ...formData, paces: e.target.value })}
                        onBlur={() => {
                          const error = validatePaces(formData.paces);
                          if (error) setValidationMessage(error);
                        }}
                      />
                      <label className="sbjt-form-label">Number of Paces</label>
                    </div>
                    <div className="sbjt-form-field">
                      <input
                        className="sbjt-form-input"
                        type="text"
                        placeholder=" "
                        value={formData.paceNumbers}
                        onChange={(e) => setFormData({ ...formData, paceNumbers: e.target.value })}
                        onBlur={() => {
                          const error = validatePaceNumbers(formData.paceNumbers);
                          if (error) setValidationMessage(error);
                        }}
                      />
                      <label className="sbjt-form-label">Pace Numbers (optional)</label>
                      <span className="sbjt-form-hint">Enter a single number or range (e.g., 1097-1108)</span>
                    </div>
                    <div className="sbjt-form-field">
                      <input
                        className="sbjt-form-input"
                        type="number"
                        placeholder=" "
                        min="1"
                        max="50"
                        value={formData.credits}
                        onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                        onBlur={() => {
                          const error = validateCredits(formData.credits);
                          if (error) setValidationMessage(error);
                        }}
                      />
                      <label className="sbjt-form-label">Credits</label>
                    </div>
                  </>
                )}
              </div>
              <div className="sbjt-modal-footer">
                <button className="sbjt-modal-btn sbjt-modal-btn--secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button className="sbjt-modal-btn sbjt-modal-btn--primary" onClick={handleSubmit}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit Modal ── */}
        {showEditModal && (
          <div className="sbjt-modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="sbjt-modal-content" onClick={e => e.stopPropagation()}>
              <div className="sbjt-modal-header sbjt-modal-header--info">
                <h5 className="sbjt-modal-title">
                  Edit {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
                </h5>
                <button className="sbjt-modal-close" onClick={() => setShowEditModal(false)} aria-label="Close">×</button>
              </div>
              <div className="sbjt-modal-body">
                {validationMessage && (
                  <div className="sbjt-form-alert sbjt-form-alert--danger">{validationMessage}</div>
                )}
                <div className="sbjt-form-field">
                  <input
                    className="sbjt-form-input"
                    type={modalType === 'class' ? 'number' : 'text'}
                    placeholder=" "
                    value={formData.name}
                    min={modalType === 'class' ? 6 : undefined}
                    max={modalType === 'class' ? 12 : undefined}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onBlur={() => {
                      let error = '';
                      if (modalType === 'class') error = validateClass(formData.name);
                      else if (modalType === 'stream') error = validateStream(formData.name);
                      else if (modalType === 'subject') error = validateSubject(formData.name);
                      if (error) setValidationMessage(error);
                    }}
                  />
                  <label className="sbjt-form-label">
                    {modalType.charAt(0).toUpperCase() + modalType.slice(1)} Name
                  </label>
                </div>

                {modalType === 'subject' && (
                  <>
                    <div className="sbjt-form-field">
                      <input
                        className="sbjt-form-input"
                        type="number"
                        placeholder=" "
                        min="1"
                        max="50"
                        value={formData.paces}
                        onChange={(e) => setFormData({ ...formData, paces: e.target.value })}
                        onBlur={() => {
                          const error = validatePaces(formData.paces);
                          if (error) setValidationMessage(error);
                        }}
                      />
                      <label className="sbjt-form-label">Number of Paces</label>
                    </div>
                    <div className="sbjt-form-field">
                      <input
                        className="sbjt-form-input"
                        type="text"
                        placeholder=" "
                        value={formData.paceNumbers}
                        onChange={(e) => setFormData({ ...formData, paceNumbers: e.target.value })}
                        onBlur={() => {
                          const error = validatePaceNumbers(formData.paceNumbers);
                          if (error) setValidationMessage(error);
                        }}
                      />
                      <label className="sbjt-form-label">Pace Numbers (optional)</label>
                      <span className="sbjt-form-hint">Enter a single number or range (e.g., 1097-1108)</span>
                    </div>
                    <div className="sbjt-form-field">
                      <input
                        className="sbjt-form-input"
                        type="number"
                        placeholder=" "
                        min="1"
                        max="50"
                        value={formData.credits}
                        onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                        onBlur={() => {
                          const error = validateCredits(formData.credits);
                          if (error) setValidationMessage(error);
                        }}
                      />
                      <label className="sbjt-form-label">Credits</label>
                    </div>
                  </>
                )}
              </div>
              <div className="sbjt-modal-footer">
                <button className="sbjt-modal-btn sbjt-modal-btn--secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button className="sbjt-modal-btn sbjt-modal-btn--primary" onClick={handleUpdate}>
                  Update
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Confirmation Modal ── */}
        {showDeleteModal && (
          <div className="sbjt-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="sbjt-modal-content" onClick={e => e.stopPropagation()}>
              <div className="sbjt-modal-header sbjt-modal-header--danger">
                <h5 className="sbjt-modal-title sbjt-modal-title--danger">Confirm Delete</h5>
                <button className="sbjt-modal-close" onClick={() => setShowDeleteModal(false)} aria-label="Close">×</button>
              </div>
              <div className="sbjt-modal-body">
                <div className="sbjt-delete-alert">
                  Are you sure you want to delete this {modalType}:{' '}
                  <strong>{currentItem?.name}</strong>?
                </div>

                {modalType === 'class' && (
                  <div className="sbjt-delete-warning">
                    <p className="sbjt-delete-warning-title">Warning: This will also delete:</p>
                    <ul className="sbjt-delete-warning-list">
                      <li>All streams in this class</li>
                      <li>All subjects in those streams</li>
                    </ul>
                  </div>
                )}
                {modalType === 'stream' && (
                  <div className="sbjt-delete-warning">
                    <p className="sbjt-delete-warning-title">Warning: This will also delete:</p>
                    <ul className="sbjt-delete-warning-list">
                      <li>All subjects in this stream</li>
                    </ul>
                  </div>
                )}

                {validationMessage && (
                  <div className="sbjt-form-alert sbjt-form-alert--danger">{validationMessage}</div>
                )}
              </div>
              <div className="sbjt-modal-footer sbjt-modal-footer--dark">
                <button className="sbjt-modal-btn sbjt-modal-btn--secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button className="sbjt-modal-btn sbjt-modal-btn--danger" onClick={handleDelete}>
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
       {userRole === 'admin' && ( 
            <SubjectsGuideModal />
       )}
        
    </div>
  );
}

export default Courses;